import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, Loader2 } from 'lucide-react'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { formatRelativeTimeIso } from '@/lib/time-format'
import {
  useInfiniteNotifications,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  type NotificationItem,
} from '@/hooks/use-notifications'
import { useFocusStore } from '@/hooks/use-focus-store'
import { audienceLabel } from '@/components/notifications/notification-utils'

type Filter = 'all' | 'unread'

export function NotificationsPage() {
  const [filter, setFilter] = useState<Filter>('all')
  const navigate = useNavigate()
  const query = useInfiniteNotifications({ unreadOnly: filter === 'unread' })
  const markRead = useMarkNotificationRead()
  const markAllRead = useMarkAllNotificationsRead()
  const { focus } = useFocusStore()

  const pages = query.data?.pages ?? []
  const items = pages.flatMap((page) => page.items)
  const total = pages[0]?.total ?? 0
  const unreadCount = pages[0]?.unread_count ?? 0
  const showAudienceChips = (focus?.available_foci?.length ?? 0) > 1

  const handleItemClick = (item: NotificationItem) => {
    if (!item.read_at) {
      markRead.mutate(item.id)
    }
    if (item.action_url) {
      navigate(item.action_url)
    }
  }

  return (
    <PageContainer maxWidth="4xl" className="py-10">
      <div className="mx-auto w-full max-w-[720px] space-y-6">
        <PageHeader
          title="Notifications"
          description="Activity on your applications and jobs."
          icon={<Bell className="h-5 w-5" />}
        />

        <div className="flex items-center justify-between">
          <div className="inline-flex items-center gap-1 rounded-[var(--radius-button)] bg-muted p-0.5">
            {(['all', 'unread'] as const).map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setFilter(value)}
                className={cn(
                  'rounded-[calc(var(--radius-button)-2px)] px-3 py-1 text-xs font-medium transition-colors',
                  filter === value
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {value === 'all' ? 'All' : 'Unread'}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => markAllRead.mutate()}
            disabled={markAllRead.isPending || unreadCount === 0}
          >
            Mark all read
          </Button>
        </div>

        <div className="overflow-hidden rounded-md border bg-background">
          {query.isLoading ? (
            <div className="space-y-3 p-4" aria-label="Loading notifications">
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton key={index} className="h-14 w-full" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-muted-foreground">
              {filter === 'unread'
                ? "You're all caught up."
                : 'No notifications yet.'}
            </p>
          ) : (
            items.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => handleItemClick(item)}
                className="flex w-full items-start gap-3 border-b px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-muted/60"
              >
                <span
                  aria-hidden
                  className={cn(
                    'mt-2 h-2 w-2 shrink-0 rounded-full',
                    item.read_at ? 'bg-transparent' : 'bg-primary'
                  )}
                />
                <span className="min-w-0 flex-1">
                  <span
                    className={cn(
                      'block text-sm',
                      item.read_at ? 'text-muted-foreground' : 'font-medium'
                    )}
                  >
                    {item.title}
                  </span>
                  {item.body && (
                    <span className="mt-0.5 block text-xs text-muted-foreground">
                      {item.body}
                    </span>
                  )}
                  <span className="mt-1 flex items-center gap-1.5 text-[11px] text-muted-foreground/80">
                    {formatRelativeTimeIso(item.created_at)}
                    {showAudienceChips && item.audience && (
                      <span className="rounded-full border border-border px-1.5 py-px text-[10px] font-medium leading-none text-muted-foreground">
                        {audienceLabel(item.audience)}
                      </span>
                    )}
                  </span>
                </span>
              </button>
            ))
          )}
        </div>

        {query.hasNextPage && (
          <div className="flex justify-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => query.fetchNextPage()}
              disabled={query.isFetchingNextPage}
            >
              {query.isFetchingNextPage ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                  Loading…
                </>
              ) : (
                `Load more (${items.length} of ${total})`
              )}
            </Button>
          </div>
        )}
      </div>
    </PageContainer>
  )
}
